import axios from 'axios';
import DOMPurify from 'dompurify';

export const apiUrl = (
  process.env.VUE_APP_API_URL || 'https://controle.gassgroup.com.br/api'
).replace(/\/$/, '');
export const imageBaseUrl = (
  process.env.VUE_APP_IMG_URL || 'https://gassgroup.com.br/imagensproducao'
).replace(/\/$/, '');
const normalizeKey = (key) => String(key).replace(/_/g, '').toLowerCase();

export function rawValue(source, key) {
  if (!source) return undefined;
  const found = Object.keys(source).find((k) => normalizeKey(k) === normalizeKey(key));
  return found === undefined ? undefined : source[found];
}

export function contentValue(source, key) {
  const value = rawValue(source, key);
  return value === undefined || value === null || value === 'NULL' ? '' : value;
}

export function boolValue(value, missing = true) {
  if (value === undefined || value === null || value === '') return missing;
  return value === true || value === 1 || String(value).toLowerCase() === 'true' || value === '1';
}

export function isVisible(item, key = 'iti_visivel') {
  return boolValue(rawValue(item, key));
}

export function sortVisible(
  items,
  orderKey = 'iti_ordem',
  visibleKey = orderKey.replace('ordem', 'visivel'),
) {
  return (Array.isArray(items) ? items : [])
    .filter((item) => isVisible(item, visibleKey))
    .slice()
    .sort((a, b) => (Number(rawValue(a, orderKey)) || 0) - (Number(rawValue(b, orderKey)) || 0));
}

export function plainText(value) {
  const el = document.createElement('div');
  el.innerHTML = DOMPurify.sanitize(String(value ?? ''));
  return (el.textContent || '').replace(/\s+/g, ' ').trim();
}

export function safeHtml(value) {
  return DOMPurify.sanitize(String(value ?? ''), {
    FORBID_TAGS: ['style', 'script', 'iframe', 'form', 'input', 'button'],
    FORBID_ATTR: ['style', 'srcset', 'id'],
  });
}

const contentRoutes = new Map();

export function safeLink(value) {
  const text = String(value ?? '').trim();
  if (!text || text === '0' || [...text].some((char) => char.charCodeAt(0) < 32 || char === '\\'))
    return '';
  if (/^(https?:|mailto:|tel:)/i.test(text)) return text;
  if (/^[a-z][a-z0-9+.-]*:/i.test(text) || text.startsWith('//')) return '';
  if (text.startsWith('#')) return text;
  const path = text
    .replace(/^\.\//, '')
    .replace(/\.html?(?=\?|#|$)/i, '')
    .replace(/^\/?index(?=\?|#|$)/i, '');
  const normalized = '/' + path.replace(/^\/+/, '');
  if (normalized === '/empresas-familiares' || normalized === '/setores#empresas-familiares')
    return '/empresas-familiares';
  return contentRoutes.get(normalized) || normalized;
}

export function imagePath(value) {
  const text = String(value ?? '').trim();
  if (!text) return '';
  if (/^https?:\/\//i.test(text)) return text;
  if (/^[a-z][a-z0-9+.-]*:/i.test(text) || text.startsWith('//')) return '';
  return `${imageBaseUrl}/${text.replace(/^\/+/, '')}`;
}

export function imagesFor(item, screen) {
  const images = rawValue(item, 'insInstitucionalImagens') || rawValue(item, 'imagens') || [];
  const lists = screen?.listasImagens;
  return sortVisible(images, 'itg_ordem').filter((image) => {
    const list = rawValue(image, 'insListaGaleriaInstImagens');
    if (list && !isVisible(list, 'lgi_visivel')) return false;
    if (lists) {
      const id = rawValue(list, 'lgi_id') ?? rawValue(image, 'lgi_id');
      const definition =
        lists.find((l) => String(l.id) === String(id)) ||
        (id == null && lists.length === 1 ? lists[0] : null);
      if (!definition || !definition.visivel) return false;
    }
    return Boolean(imagePath(rawValue(image, 'itg_urlimagem')));
  });
}

export function firstImage(item, screen) {
  const first = imagesFor(item, screen)[0];
  return first ? imagePath(rawValue(first, 'itg_urlimagem')) : '';
}

export const itemId = (item) => contentValue(item, 'iti_id');
export const slug = (value) =>
  plainText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
export function detailPath(type, item) {
  const id = itemId(item);
  return id
    ? `/${type}/${encodeURIComponent(id)}/${slug(contentValue(item, 'iti_nome')) || id}`
    : '';
}

const pending = new Map();
export async function getInstitutionalContent(id, respectVisibility = true, randomCount = 0) {
  const key = `${id}:${respectVisibility}:${randomCount}`;
  // Share simultaneous requests only: later navigation fetches current visibility/content.
  if (!pending.has(key)) {
    pending.set(
      key,
      axios
        .get(`${apiUrl}/Public/content/institucional/${id}${randomCount ? '/random' : ''}`, {
          timeout: 20000,
          params: randomCount ? { count: randomCount } : undefined,
        })
        .then((response) => {
          const data = response.data?.data ?? response.data?.result ?? response.data;
          const records = Array.isArray(data) ? data : data ? [data] : [];
          return respectVisibility
            ? sortVisible(records)
            : records
                .slice()
                .sort(
                  (a, b) =>
                    (Number(rawValue(a, 'iti_ordem')) || 0) -
                    (Number(rawValue(b, 'iti_ordem')) || 0),
                );
        })
        .finally(() => pending.delete(key)),
    );
  }
  return pending.get(key);
}

export async function loadScreens(definitions, randomCounts = {}) {
  const active = definitions.filter((s) => s.visivel);
  const results = await Promise.allSettled(
    active.map((s) =>
      getInstitutionalContent(
        s.id,
        s.multi || s.campos.some((field) => field.campo === 'iti_visivel'),
        randomCounts[s.id] || 0,
      ),
    ),
  );
  const data = {},
    errors = [];
  active.forEach((screen, i) => {
    const result = results[i];
    data[screen.chave] = result.status === 'fulfilled' ? result.value : [];
    if (screen.chave === 'solucoes-especialidades-lista') {
      contentRoutes.clear();
      for (const item of data[screen.chave]) {
        const name = slug(contentValue(item, 'iti_nome'));
        if (name) contentRoutes.set('/' + name, detailPath('solucoes', item));
        if (name === 'consultoria-legal')
          contentRoutes.set('/advocacia', detailPath('solucoes', item));
      }
    }
    if (result.status === 'rejected') errors.push(screen.chave);
  });
  return { data, errors };
}

export async function sendContact(form) {
  return axios.post(`${apiUrl}/Public/contact/send`, form, { timeout: 30000 });
}
